import React, { useState, useEffect, useContext, useRef } from 'react';
import {
    Card,
    CardBody,
    Table,
    Row,
    Col,
    Badge,
    Button,
    FormGroup,
    Label,
    InputGroupAddon,
    InputGroup,
} from 'reactstrap';
import { Icon } from '@iconify/react';
import calendarRange from '@iconify/icons-mdi/calendar-range';
import SimpleBar from 'simplebar-react';
import arrowsVertical from '@iconify/icons-carbon/arrows-vertical';
import arrowUp from '@iconify/icons-carbon/arrow-up';
import arrowDown from '@iconify/icons-carbon/arrow-down';
import commentText from '@iconify/icons-mdi/comment-text';
import homeCity from '@iconify/icons-mdi/home-city';
import noteMultipleOutline from '@iconify/icons-mdi/note-multiple-outline';
import pillIcon from '@iconify/icons-mdi/pill';
import hospitalBox from '@iconify/icons-mdi/hospital-box';
import TableRowDetailModal from './TableRowDetailModal';
import { useAtom } from 'jotai';
import { WidgetsData } from '../data/WidgetsData';
import { ActiveAlertContext } from '../contexts/ActiveAlertContext';
import FlatPicker from 'react-flatpickr';
import 'flatpickr/dist/themes/dark.css';
import LastUpdated from '../utility-components/LastUpdatedComponent';
import { SingleWidgetActiveTabContext } from '../contexts/SingleWidgetActiveTabContext';
import refreshIcon from '@iconify/icons-mdi/refresh';
import transferRight from '@iconify/icons-mdi/transfer-right';
import { SrxApi } from '../api/SrxApi';
import { SelectedFacilitiesContext } from '../contexts/SelectedFacilitiesContext';
import transferLeft from '@iconify/icons-mdi/transfer-left';
import { ShowResolvedAlertsContext } from '../contexts/ShowResolvedAlertsContexts';
import { ShowAllFacilitiesContext } from '../contexts/ShowAllFacilitiesContext';
import { AlertSearchContext } from '../contexts/AlertSearchContext';
import classNames from 'classnames';
import AlertSearchBar from '../components/AlertSearchBar';
import { fetchWidgetAlertsWithDummyFallback } from '../utility-services/fetchWidgetAlertsWithDummyFallback';

const SingleAlertWidgetV2 = (props) => {
    const { selectedFacilities } = useContext(SelectedFacilitiesContext);

    const { activeAlert, setActiveAlert } = useContext(ActiveAlertContext);

    const { activeTab, setActiveTab } = useContext(SingleWidgetActiveTabContext);

    const [archiveMode, setArchiveMode] = useState(false);
    const { searchValue, setSearchValue } = useContext(AlertSearchContext);

    const { showResolved, setShowResolved } = useContext(ShowResolvedAlertsContext);

    const { showAllFacilities } = useContext(ShowAllFacilitiesContext);

    // Date filter state - null by default, reset on navigation/mode change
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    useEffect(() => {
        setArchiveMode(props.location.pathname.split('/').pop() === 'archive');
    }, [props.location.pathname]);

    // Reset date filters when switching between current and archive or navigating to different widget
    useEffect(() => {
        setFromDate(null);
        setToDate(null);
    }, [activeWidget, archiveMode]);

    // const [singleWidgetInfo, setSingleWidgetInfo] = useState(widgetInfo);

    const getActiveWidget = () => {
        const paths = props.location.pathname.split('/');

        const guess = paths.pop();

        return guess === 'archive' ? paths.pop() : guess;
    };

    var activeWidget = getActiveWidget();

    const widgetData = WidgetsData.find((widget) => widget.path.split('/').pop() === activeWidget);

    const isArchiveRoute = props.location.pathname.split('/').pop() === 'archive';
    const activeAtom = isArchiveRoute && widgetData.archived?.atom ? widgetData.archived.atom : widgetData.atom;
    const [data, setData] = useAtom(activeAtom);

    // const [filteredAlerts, setFilteredAlerts] = useAtom(widgetData.atomFilteredAlerts);

    const [isModalOpen, setIsModalOpen] = useState(false);

    // const [activeTab, setActiveTab] = useState('current');

    const query = new URLSearchParams(props.location.search);

    useEffect(() => {
        setActiveTab('current');
    }, [props.location.search]);

    useEffect(() => {
        setReactSucksHook(!reactSucksHook);
        loadData();
    }, [selectedFacilities, showResolved, showAllFacilities, searchValue]);

    // Reload data when date filters change
    useEffect(() => {
        if (archiveMode) {
            setReactSucksHook(!reactSucksHook);
            loadData();
        }
    }, [fromDate, toDate]);

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (activeAlert) setIsModalOpen(true);
        else setIsModalOpen(false);
    }, [activeAlert]);

    const firstRender = useRef(true);

    const toggleModal = () => {
        if (activeAlert) setActiveAlert(null);
        setIsModalOpen(!isModalOpen);
    };

    // function getDateAndTime(date) {
    //     let onlyDate = new Date(date).toLocaleString();
    //     return onlyDate;
    // }

    function formatDate(inputDate) {
        let date, month, year;

        date = inputDate.getDate();
        month = inputDate.getMonth() + 1;
        year = inputDate.getFullYear();

        date = date.toString().padStart(2, '0');
        month = month.toString().padStart(2, '0');

        return `${year}-${month}-${date}`;
    }

    const handleDatePickerFromChange = (date) => {
        if (date && date[0]) {
            const formattedDate = formatDate(date[0]);
            setFromDate(formattedDate);
        } else {
            setFromDate(null);
        }
    };

    const handleDatePickerToChange = (date) => {
        if (date && date[0]) {
            const formattedDate = formatDate(date[0]);
            setToDate(formattedDate);
        } else {
            setToDate(null);
        }
    };

    // Widget detail and columns come from WidgetsData (widgetData).
    const widgetInfo = widgetData;

    const [sortBy, setSortBy] = useState({
        sortField: widgetData.defaultSortField,
        sortDirection: 'desc',
    });

    const [reactSucksHook, setReactSucksHook] = useState(false);

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }

        loadData();
    }, [sortBy]);

    useEffect(() => {
        const intervalId = setInterval(refreshData, 30000);

        return () => clearInterval(intervalId); // Cleanup on unmount or dependency change
    }, [reactSucksHook, searchValue]);

    const onSort = (field) => {
        let direction = 'asc';

        if (field === sortBy.sortField) {
            direction = sortBy.sortDirection === 'asc' ? 'desc' : 'asc';
        }

        setData((currentData) => ({
            ...currentData,
            isSorted: true,
        }));

        setSortBy({
            sortField: field,
            sortDirection: direction,
        });

        setReactSucksHook(!reactSucksHook);
    };

    const SortCarets = () => (
        <>
            {sortBy.sortDirection === 'asc' ? (
                <Icon icon={arrowUp} color="black" />
            ) : (
                <Icon icon={arrowDown} color="black" />
            )}
        </>
    );

    const [rotateRefreshButton, setRotateRefreshButton] = useState(false);

    const alertsUrl = (more = false) => {
        const path = getUrl();
        const params = [];

        if (archiveMode || props.location.pathname.split('/').pop() === 'archive') {
            params.push('archived=true');
        }

        if (data.isSorted) {
            params.push(`sort=${sortBy.sortField}`, `sortOrder=${sortBy.sortDirection}`);
            if (more) params.push(`page=${data.nextPage}`);
        } else if (more) {
            // Use cursor pagination only if the widget supports it
            if (widgetData.usesCursorPaging !== false) {
                params.push(`cursor=${data.cursor}`);
            } else {
                params.push(`page=${data.nextPage}`);
            }
        }

        // IF facilities is not null add the facilities to the query parameter, a new query parameter will be added for each facility not separated by commas
        if (selectedFacilities) {
            selectedFacilities.forEach((facility) => {
                params.push(`facilityIds=${facility.facID}`);
            });
        }

        // Add date filters to URL if they exist
        if (fromDate) {
            params.push(`fDate=${fromDate}`);
        }
        if (toDate) {
            params.push(`eDate=${toDate}`);
        }

        if (showResolved) {
            params.push('hideResolved=false');
        }

        if (showAllFacilities) {
            params.push('showAll=true');
        }

        if (searchValue) {
            params.push(`searchValue=${encodeURIComponent(searchValue)}`);
        }

        return params.length ? `${path}?${params.join('&')}` : path;
    };

    const loadData = async (more = false) => {
        const newData = widgetData.dummyData
            ? await fetchWidgetAlertsWithDummyFallback(alertsUrl(more), widgetData.dummyData)
            : (await SrxApi.get(alertsUrl(more))).data;

        setData((currentData) => ({
            ...currentData,
            alertData: more ? [...currentData.alertData, ...newData.results] : newData.results,
            cursor: newData.cursor,
            lastRefreshed: newData.timeStamp,
            loading: false,
            error: false,
            alertCount: currentData.alertCount,
            filteredCount: newData.count,
            nextPage: newData.nextPage,
            isSorted: currentData.isSorted,
        }));
    };

    const refreshData = async () => {
        const _url = new URL('https://placeholder.com' + alertsUrl());

        _url.searchParams.delete('page');

        _url.searchParams.delete('cursor');

        const freshData = widgetData.dummyData
            ? await fetchWidgetAlertsWithDummyFallback(_url.pathname + _url.search, widgetData.dummyData)
            : (await SrxApi.get(_url.pathname + _url.search)).data;

        setData((currentData) => {
            // Extract the top {freshData.length} alerts from CURRENT data (not stale closure)
            const topAlerts = currentData.alertData.slice(0, freshData.results.length);

            // Filter out alerts in freshData that are already in topAlerts
            const newData = freshData.results.filter(
                (alert) => !topAlerts.some((oldAlert) => oldAlert.rxGuid === alert.rxGuid)
            );

            // Add new alerts to the top of the CURRENT alertData
            const updatedData = newData.length ? [...newData, ...currentData.alertData] : currentData.alertData;

            // This is not 100% accurate because if we received new data
            // and {page} is used for pagination,
            // we will end up skipping a few records.
            // But it's close enough
            return {
                ...currentData,
                alertData: updatedData,
                cursor: currentData.cursor,
                lastRefreshed: freshData.timeStamp,
                loading: false,
                error: false,
                alertCount: currentData.alertCount,
                filteredCount: freshData.count,
                nextPage: currentData.nextPage,
                isSorted: currentData.isSorted,
            };
        });
    };

    const getUrl = () => {
        return props.location.pathname.replace('/archive', '').replace('/alerts', '');
    };

    return (
        <>
            <Row className="mt-2 mb-1  justify-content-between">
                <Col className="d-flex align-items-center">
                    <h3>{widgetInfo.sidebarName || widgetInfo.name}</h3>
                    <Button
                        color="secondary"
                        className={`ml-2 py-0 px-1 d-flex align-items-center`}
                        style={{ right: '20px' }}
                        onClick={() => {
                            setRotateRefreshButton(true);
                            refreshData();
                        }}
                        title="Refresh Alerts">
                        <Icon
                            className={`${rotateRefreshButton && 'refresh-widget-button-rotate'}`}
                            onAnimationEnd={() => setRotateRefreshButton(false)}
                            icon={refreshIcon}
                            width="25"
                        />
                    </Button>
                </Col>
                <Col sm={'auto'}>
                    {!archiveMode ? (
                        <Button
                            color="dark"
                            onClick={() => {
                                props.history.push(props.location.pathname + '/archive');
                            }}>
                            <span className="mr-1">
                                <b>Archive</b>
                            </span>
                            <Icon icon={transferRight} width="30" />
                        </Button>
                    ) : (
                        <Button
                            color="dark"
                            onClick={() => {
                                props.history.push(props.location.pathname.replace('/archive', ''));
                            }}>
                            <Icon icon={transferLeft} width="30" />
                            <span className="ml-1">
                                <b>Current</b>
                            </span>
                        </Button>
                    )}
                </Col>
            </Row>
            <Row className="mb-2">
                <Col>
                    <AlertSearchBar
                        label="Search"
                        searchPlaceholder="rx no, patient name, drug name"
                        value={searchValue}
                        onSearch={(value) => setSearchValue((value || '').trim())}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <Card
                        style={{
                            minHeight: '410.61px',
                            opacity: widgetInfo.active ? '' : '.30',
                            pointerEvents: widgetInfo.active ? '' : 'none',
                        }}>
                        <div
                            className="d-flex justify-content-between mt-2 mx-3 align-items-center"
                            style={{ minHeight: '38px' }}>
                            <div className="d-flex">
                                {archiveMode ? (
                                    <>
                                        <FormGroup className="mb-0 d-flex">
                                            <Label
                                                className="d-flex align-items-center mb-0 mr-2"
                                                style={{ whiteSpace: 'nowrap' }}>
                                                From:
                                            </Label>
                                            <InputGroup style={{ width: '160px' }}>
                                                <FlatPicker
                                                    className="form-control rounded-left bg-white"
                                                    value={fromDate ? [fromDate] : null}
                                                    onChange={(date) => {
                                                        handleDatePickerFromChange(date);
                                                    }}
                                                    options={{
                                                        maxDate: toDate || 'today',
                                                        altInput: true,
                                                        altFormat: 'n/j/y',
                                                        dateFormat: 'Y-m-d',
                                                    }}
                                                    placeholder="Choose date..."
                                                />
                                                <InputGroupAddon addonType="append">
                                                    <span className="input-group-text py-0 px-1">
                                                        <Icon icon={calendarRange} width="25px" />
                                                    </span>
                                                </InputGroupAddon>
                                            </InputGroup>
                                        </FormGroup>
                                        <FormGroup className="mb-0 d-flex ml-2">
                                            <Label
                                                className="d-flex align-items-center mb-0 mr-2"
                                                style={{ whiteSpace: 'nowrap' }}>
                                                To:
                                            </Label>
                                            <InputGroup style={{ width: '160px' }}>
                                                <FlatPicker
                                                    className="form-control rounded-left bg-white"
                                                    value={toDate ? [toDate] : null}
                                                    onChange={(date) => {
                                                        handleDatePickerToChange(date);
                                                    }}
                                                    options={{
                                                        maxDate: 'today',
                                                        minDate: fromDate,
                                                        altInput: true,
                                                        altFormat: 'n/j/y',
                                                        dateFormat: 'Y-m-d',
                                                    }}
                                                    placeholder="Choose date..."
                                                />
                                                <InputGroupAddon addonType="append">
                                                    <span className="input-group-text py-0 px-1">
                                                        <Icon icon={calendarRange} width="25px" />
                                                    </span>
                                                </InputGroupAddon>
                                            </InputGroup>
                                        </FormGroup>
                                    </>
                                ) : (
                                    <Badge className="py-1 px-2" color="light">
                                        <LastUpdated lastRefreshed={data.lastRefreshed} widgetPath={widgetInfo.path} />
                                    </Badge>
                                )}
                            </div>
                            <div style={{ fontSize: '16px',}}>
                                Total: <b>{data.filteredCount}</b>
                            </div>
                        </div>
                        <SimpleBar className="single-widget-height mx-2 my-2">
                            <CardBody className="pb-1 px-1 pt-0">
                                <Table hover size="sm" className="mb-1">
                                    <thead style={{ position: 'sticky', top: '0' }} className="bg-light alert-th">
                                        <tr style={{ overflow: 'hidden', whiteSpace: 'nowrap', fontSize: '15px' }}>
                                            {widgetInfo.columns.map((col, colIndex) => (
                                                <th
                                                    key={colIndex}
                                                    className={`py-2 position-relative ${col.sort ? '' : 'noHover'}`}
                                                    onClick={col.sort ? () => onSort(col.key) : null}>
                                                    {col.icon && <Icon icon={col.icon} />} {col.name}{' '}
                                                    {col.sort ? (
                                                        sortBy.sortField === col.key ? (
                                                            <SortCarets />
                                                        ) : (
                                                            <Icon icon={arrowsVertical} style={{ opacity: '0.6' }} />
                                                        )
                                                    ) : (
                                                        ''
                                                    )}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <>
                                            {data.loading ? (
                                                <tr>
                                                    <td colSpan={widgetInfo.columns.length}>
                                                        <div>
                                                            <h5 className="text-center">Loading Alerts</h5>
                                                            <div className="bouncing-loader">
                                                                <div></div>
                                                                <div></div>
                                                                <div></div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                data.alertData.map((alert, index) => (
                                                    <tr
                                                        key={alert.rxGuid}
                                                        style={{ fontSize: '13px' }}
                                                        className={classNames(
                                                            { unreadRow: !alert.readByFaci },
                                                            { readRow: alert.readByFaci },
                                                            { resolvedRow: alert.srxStatus || alert.pharmacyStatus }
                                                        )}
                                                        onClick={() => {
                                                            setActiveAlert({
                                                                name: widgetInfo.sidebarName || widgetInfo.name,
                                                                alert: alert,
                                                                stateName: widgetInfo.stateName,
                                                                category: widgetInfo.category,
                                                                requestActionPermissionWidgetName:
                                                                    widgetInfo.requestActionPermissionWidgetName,
                                                                tab: activeTab,
                                                            });
                                                        }}>
                                                        {widgetInfo.columns.map((col, colIndex) => (
                                                            <td
                                                                key={colIndex}
                                                                style={{ verticalAlign: 'middle' }}
                                                                className={col?.className}>
                                                                {col.prefix}
                                                                {col.format
                                                                    ? col.format(alert[col.key], alert.rxGuid, alert)
                                                                    : alert[col.key]}
                                                                {col.suffix}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            )}
                                            <tr>
                                                <td colSpan="100%" align="center">
                                                    {data.alertData.length ? (
                                                        data.nextPage ? (
                                                            <Button color="link" onClick={() => loadData(true)}>
                                                                <b>Show more...</b>
                                                            </Button>
                                                        ) : (
                                                            <span>
                                                                <b>End of alerts</b>
                                                            </span>
                                                        )
                                                    ) : (
                                                        <span>
                                                            <b>No alerts</b>
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        </>
                                    </tbody>
                                </Table>
                            </CardBody>
                        </SimpleBar>
                    </Card>
                    {activeAlert && (
                        <TableRowDetailModal isOpen={isModalOpen} toggle={toggleModal} alertInfo={activeAlert} />
                    )}
                </Col>
            </Row>
        </>
    );
};

export default SingleAlertWidgetV2;
